import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MF_POPUP_FLAG, MF_POPUP_MESSAGE } from "../../Helpers/mfPopupReturn";

/**
 * The unguarded landing strip every MF payment and e-mandate provider returns
 * to. `public/mf-postback-sw.js` rewrites a POST callback to a GET on this same
 * URL, so both shapes arrive here.
 *
 * Two branches:
 *
 *  • redirect — the investor dashboard's flow. The tab navigated away, so the
 *    callback parameters are carried back onto the dashboard route.
 *  • popup    — the Portfolio Planner's flow. The plan lives in memory in the
 *    opener, so this window hands its parameters over and closes instead.
 *
 * The return address is validated against a fixed allow-list of admin routes
 * before anything is echoed onto it.
 */

const RETURN_PATHS = [
  /^\/mf_investor_profile\/[^/]+\/investments$/,
  /^\/mf_portfolio_planner(\/[^/]+)?$/,
];

const MFPaymentReturn: React.FC = () => {
  const location = useLocation();
  const [error, setError] = useState("");
  const [popupMode, setPopupMode] = useState(false);

  useEffect(() => {
    try {
      const callback = new URLSearchParams(location.search);
      const returnPath = callback.get("mf_admin_return") || "";
      const target = new URL(returnPath, window.location.origin);
      if (
        target.origin !== window.location.origin ||
        !RETURN_PATHS.some((pattern) => pattern.test(target.pathname))
      ) {
        throw new Error("The admin return address is invalid.");
      }
      callback.delete("mf_admin_return");
      callback.forEach((value, key) => {
        if (key.startsWith("mf_admin_")) target.searchParams.set(key, value);
      });

      if (target.searchParams.get(MF_POPUP_FLAG) === "1") {
        setPopupMode(true);
        const params: Record<string, string> = {};
        target.searchParams.forEach((value, key) => {
          params[key] = value;
        });
        if (!window.opener || window.opener.closed) {
          throw new Error(
            "The window that started this authorization is no longer open. Re-open the planner and check the investor's Orders before retrying."
          );
        }
        window.opener.postMessage(
          { type: MF_POPUP_MESSAGE, params },
          window.location.origin
        );
        window.setTimeout(() => window.close(), 250);
        return;
      }

      window.location.replace(`${target.pathname}${target.search}`);
    } catch (callbackError) {
      setError(
        (callbackError as Error).message ||
          "The payment return could not be processed."
      );
    }
  }, [location.search]);

  const heading = error
    ? "Unable to complete the return"
    : popupMode
    ? "Authorization received — closing this window…"
    : "Returning to the MF dashboard…";

  return (
    <main style={{ minHeight: "70vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ textAlign: "center" }}>
        <h2>{heading}</h2>
        {error && <p>{error}</p>}
      </div>
    </main>
  );
};

export default MFPaymentReturn;
